import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, Users } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AccountStatusBadge } from "@/components/AccountStatusBadge";
import { cn, initials } from "@/lib/utils";
import { useWorkspace } from "@/store/workspace";
import { AddAccountDialog } from "@/components/AddAccountDialog";

export function AccountSidebar() {
  const qc = useQueryClient();
  const { selectedAccountId, selectAccount } = useWorkspace();
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);

  const { data: accounts = [] } = useQuery({
    queryKey: ["accounts"],
    queryFn: api.listAccounts,
    refetchInterval: 30_000,
  });

  // Непрочитанное по всем аккаунтам: списки чатов клиент держит только для
  // открытого, посчитать на месте нечем. Кэш обновляет useLiveCache.
  const { data: unread = [] } = useQuery({
    queryKey: ["accounts", "unread"],
    queryFn: api.accountsUnread,
    refetchInterval: 60_000,
  });
  const unreadByAccount = useMemo(
    () => new Map(unread.map((u) => [u.account_id, u])),
    [unread]
  );

  // auto-select first active if nothing selected; reset stale persisted id
  useEffect(() => {
    if (accounts.length === 0) return;
    const valid = selectedAccountId && accounts.some((a) => a.id === selectedAccountId);
    if (valid) return;
    const pick = accounts.find((a) => a.status === "active") ?? accounts[0];
    selectAccount(pick.id);
  }, [accounts, selectedAccountId, selectAccount]);

  const filtered = useMemo(() => {
    if (!q.trim()) return accounts;
    const t = q.toLowerCase();
    return accounts.filter(
      (a) =>
        a.phone.includes(t) ||
        (a.username ?? "").toLowerCase().includes(t) ||
        (a.first_name ?? "").toLowerCase().includes(t) ||
        (a.last_name ?? "").toLowerCase().includes(t)
    );
  }, [accounts, q]);

  return (
    <aside className="flex w-72 shrink-0 flex-col border-r border-border bg-card/40">
      <div className="border-b border-border p-3">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Аккаунты</h2>
          <Button size="iconSm" variant="ghost" onClick={() => setAdding(true)} title="Добавить">
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Поиск аккаунта"
            className="pl-8 h-9"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <Empty />
        ) : (
          <ul>
            {filtered.map((a) => {
              const name = a.username
                ? `@${a.username}`
                : [a.first_name, a.last_name].filter(Boolean).join(" ") || a.phone;
              const active = selectedAccountId === a.id;
              const u = unreadByAccount.get(a.id);
              return (
                <li key={a.id}>
                  <button
                    onClick={() => selectAccount(a.id)}
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent/60",
                      active && "bg-accent text-foreground"
                    )}
                  >
                    <Avatar name={name} initials={initials(a.first_name, a.last_name, a.phone.slice(-2))} size={36} src={a.avatar_media_id ? api.mediaUrl(a.avatar_media_id) : null} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium">{name}</span>
                        <AccountStatusBadge status={a.status} title={a.last_error ?? undefined} />
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-mono text-xs text-muted-foreground">{a.phone}</span>
                        {u && (
                          <span
                            className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground"
                            title={`${u.unread_messages} непрочитанных в ${u.unread_chats} диалогах`}
                          >
                            {u.unread_messages}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <AddAccountDialog open={adding} onOpenChange={setAdding} onDone={() => qc.invalidateQueries({ queryKey: ["accounts"] })} />
    </aside>
  );
}

function Empty() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 p-8 text-center text-sm text-muted-foreground">
      <Users className="h-8 w-8 opacity-30" />
      Нет аккаунтов
    </div>
  );
}
